import { Helmet } from "react-helmet-async";
import LegalLayout from "@/components/legal/LegalLayout";

const sections = [
  { id: "how", title: "How this roadmap works" },
  { id: "now", title: "Now (in flight)" },
  { id: "next", title: "Next (this quarter)" },
  { id: "later", title: "Later (exploring)" },
  { id: "wont", title: "Not doing" },
  { id: "feedback", title: "Share feedback" },
];

const Roadmap = () => (
  <>
    <Helmet>
      <title>Product Roadmap — The Emerging Market</title>
      <meta
        name="description"
        content="What The Emerging Market is building now, next, and later for Alabama communities, partners, funders, and agencies."
      />
      <link rel="canonical" href="https://emrkt.co/roadmap" />
      <meta property="og:title" content="Product Roadmap — The Emerging Market" />
      <meta property="og:url" content="https://emrkt.co/roadmap" />
      <meta property="og:type" content="website" />
    </Helmet>
    <LegalLayout
      title="Product Roadmap"
      description="A public, plain-language view of what we're building. We share what's in flight, what's queued, and what we've decided against — so partners and members can plan alongside us."
      lastUpdated="July 3, 2026"
      sections={sections}
    >
      <section id="how">
        <h2>1. How this roadmap works</h2>
        <p>
          The Emerging Market is built for Alabama communities. Every item on
          this roadmap ladders back to a public goal: making funding, legislation,
          partners, and lessons learned easier to find, easier to act on, and
          easier to contribute back to.
        </p>
        <p>
          We publish three buckets — <strong>Now</strong>, <strong>Next</strong>,
          and <strong>Later</strong> — plus a short list of things we've
          intentionally decided <strong>not</strong> to build. Dates are targets,
          not commitments; scope may shift as we learn from members, partners,
          and pilots.
        </p>
      </section>

      <section id="now">
        <h2>2. Now (in flight)</h2>
        <ul>
          <li>
            <strong>Opportunities Hub.</strong> Broadening <em>Opportunities</em>
            beyond funding to include collaboration, partnership, and growth
            surfaces (events, credentials, sponsorships).
          </li>
          <li>
            <strong>Intelligence Tier 1.</strong> Consent-gated research
            plumbing, quarterly report drafts, and demo-request flows for
            funders and agencies at <a href="/for-funders">/for-funders</a> and{" "}
            <a href="/for-agencies">/for-agencies</a>.
          </li>
          <li>
            <strong>Legal data ingestion.</strong> Daily Congress.gov backfill
            of Alabama delegation bills is live; LegiScan (state-level) is
            pending API access.
          </li>
          <li>
            <strong>Observability.</strong> Cron heartbeats and structured
            logging rolled out across scheduled jobs; Admin Pipelines strip
            surfaces health at a glance.
          </li>
        </ul>
      </section>

      <section id="next">
        <h2>3. Next (this quarter)</h2>
        <ul>
          <li>
            <strong>Partner program auto-provisioning.</strong> Approved partner
            applications automatically create affiliate accounts and referral
            codes.
          </li>
          <li>
            <strong>Newsletter subscribe surface.</strong> Public opt-in for the
            weekly grants digest, separate from platform sign-up.
          </li>
          <li>
            <strong>Intelligence Tier 2.</strong> Reports library index,
            methodology page, and custom alert subscriptions for funders and
            agencies.
          </li>
          <li>
            <strong>Services intake → Archive pipeline.</strong> Turning the
            Services page into a live intake queue, with each engagement
            contributing case studies and lessons learned back to the Archive.
          </li>
        </ul>
      </section>

      <section id="later">
        <h2>4. Later (exploring)</h2>
        <ul>
          <li>
            <strong>Community profiles at scale.</strong> Expand beyond the
            Mobile County and Prichard pilots toward the remaining 65 Alabama
            counties as content and partnerships allow.
          </li>
          <li>
            <strong>Structured lessons-learned capture.</strong> A first-class
            "what worked / what we'd do differently" artifact on every closed
            project.
          </li>
          <li>
            <strong>White-label anchor portals.</strong> Co-branded{" "}
            <code>/anchors/:slug</code> surfaces for institutional partners.
          </li>
          <li>
            <strong>Credentialing roadmap.</strong> Tiered licensure, insurance,
            SAM.gov, DBE, and SOC 2 milestones as the platform matures.
          </li>
        </ul>
      </section>

      <section id="wont">
        <h2>5. Not doing (for now)</h2>
        <ul>
          <li>
            <strong>Multi-state expansion.</strong> We are Alabama-first by
            design. Out-of-state data may inform, but it will not define the
            product.
          </li>
          <li>
            <strong>Anonymous sign-ups or auto-confirm email.</strong> Every
            account is a verified member.
          </li>
          <li>
            <strong>General-purpose social feed.</strong> Ideas, projects, and
            lessons stay tied to Alabama communities and funding outcomes.
          </li>
        </ul>
      </section>

      <section id="feedback">
        <h2>6. Share feedback</h2>
        <p>
          Roadmap requests, corrections, and partnership ideas are welcome.
          Email <a href="mailto:info@emrkt.co">info@emrkt.co</a> or open a
          discussion inside the platform. Recently shipped work is tracked on
          the <a href="/changelog">Changelog</a>.
        </p>
      </section>
    </LegalLayout>
  </>
);

export default Roadmap;